import { Component, type ReactNode, type ErrorInfo } from 'react'
import { AlertTriangle, RefreshCw, Home } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from './Button'

/**
 * ErrorBoundary Component
 * Catches React rendering errors and displays a fallback UI.
 * Based on SHARED-ERROR-STATES.md Full Page Error pattern.
 *
 * Usage:
 *   <ErrorBoundary>
 *     <App />
 *   </ErrorBoundary>
 *
 *   <ErrorBoundary fallback={<CustomFallback />}>
 *     <Widget />
 *   </ErrorBoundary>
 */

export interface ErrorBoundaryProps {
  children: ReactNode
  /** Custom fallback UI to render on error */
  fallback?: ReactNode
  /** Callback when an error is caught */
  onError?: (error: Error, errorInfo: ErrorInfo) => void
  /** Whether to show a compact inline fallback instead of full-page */
  inline?: boolean
  className?: string
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.props.onError?.(error, errorInfo)
    console.error('[ErrorBoundary] Caught error:', error, errorInfo)
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null })
  }

  handleReload = () => {
    window.location.reload()
  }

  handleGoHome = () => {
    window.location.href = '/'
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      if (this.props.inline) {
        return <InlineErrorFallback onRetry={this.handleReset} className={this.props.className} />
      }

      return <FullPageErrorFallback onReload={this.handleReload} onGoHome={this.handleGoHome} className={this.props.className} />
    }

    return this.props.children
  }
}

/**
 * Full-page error fallback (default)
 * Self-contained — does not use useTranslation since it renders
 * outside the normal React tree when providers may have crashed.
 */
interface FullPageErrorFallbackProps {
  onReload: () => void
  onGoHome: () => void
  className?: string
}

function FullPageErrorFallback({ onReload, onGoHome, className }: FullPageErrorFallbackProps) {
  // Detect language from <html lang="..."> attribute set by I18nProvider
  const lang = document.documentElement.lang || 'bg'
  const isBg = lang === 'bg'

  const title = isBg ? 'Нещо се обърка' : 'Something went wrong'
  const description = isBg
    ? 'Възникна неочаквана грешка. Опитайте да презаредите страницата.'
    : 'An unexpected error occurred. Try reloading the page.'
  const reloadLabel = isBg ? 'Презареди страницата' : 'Reload page'
  const homeLabel = isBg ? 'Към началната страница' : 'Go to homepage'

  return (
    <div
      className={cn(
        'min-h-screen flex items-center justify-center bg-gray-50 px-6',
        className
      )}
      role="alert"
    >
      <div className="text-center max-w-md">
        <div className="w-16 h-16 mx-auto mb-6 bg-error/10 rounded-full flex items-center justify-center">
          <AlertTriangle className="w-8 h-8 text-error" />
        </div>
        <h1 className="text-h2 text-gray-900 mb-2">{title}</h1>
        <p className="text-body text-gray-500 mb-8">{description}</p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <Button onClick={onReload} leftIcon={<RefreshCw className="w-4 h-4" />}>
            {reloadLabel}
          </Button>
          <Button variant="secondary" onClick={onGoHome} leftIcon={<Home className="w-4 h-4" />}>
            {homeLabel}
          </Button>
        </div>
      </div>
    </div>
  )
}

/**
 * Inline error fallback for section-level error boundaries
 * Also self-contained for safety.
 */
interface InlineErrorFallbackProps {
  onRetry: () => void
  className?: string
}

function InlineErrorFallback({ onRetry, className }: InlineErrorFallbackProps) {
  const lang = document.documentElement.lang || 'bg'
  const isBg = lang === 'bg'

  const title = isBg ? 'Нещо се обърка' : 'Something went wrong'
  const description = isBg
    ? 'Възникна неочаквана грешка в тази секция.'
    : 'An unexpected error occurred in this section.'
  const retryLabel = isBg ? 'Опитай отново' : 'Try again'

  return (
    <div
      className={cn(
        'bg-white border border-gray-200 rounded-lg p-6 text-center',
        className
      )}
      role="alert"
    >
      <div className="w-12 h-12 mx-auto mb-4 bg-error/10 rounded-full flex items-center justify-center">
        <AlertTriangle className="w-6 h-6 text-error" />
      </div>
      <h3 className="text-h3 text-gray-900 mb-2">{title}</h3>
      <p className="text-body-sm text-gray-500 mb-4">{description}</p>
      <Button
        variant="secondary"
        size="sm"
        onClick={onRetry}
        leftIcon={<RefreshCw className="w-4 h-4" />}
      >
        {retryLabel}
      </Button>
    </div>
  )
}
